import { useMemo } from 'react';
import { lowerCase } from 'lodash';

import { getResourceTagLabel } from 'src/data/performance';

interface Resource {
  name: string;
  content?: string;
  contentType: string;
  tags?: string[];
}

interface UseFilteredResourcesParams<T extends Resource> {
  resources: T[] | null;
  searchText: string;
  typeFilter: string[];
  tagFilter: string[];
}

/**
 * Filter resources based on search text, "type" and "tag" filters
 *
 * @param params Object containing resources, searchText, typeFilter, and tagFilter
 * @returns Filtered resources array
 */
export const useFilteredResources = <T extends Resource>({
  resources,
  searchText,
  typeFilter,
  tagFilter,
}: UseFilteredResourcesParams<T>): T[] => {
  return useMemo(() => {
    if (!resources) {
      return [];
    }

    const preparedSearchText = lowerCase(searchText);

    return resources.filter((resource) => {
      const matchesSearchText =
        !searchText ||
        lowerCase(resource.name).includes(preparedSearchText) ||
        lowerCase(resource.content).includes(preparedSearchText);
      const matchesFilterType = !typeFilter.length || typeFilter.includes(resource.contentType);
      const matchesFilterTag =
        !tagFilter.length ||
        (resource.tags || []).some((tag) => tagFilter.includes(getResourceTagLabel(tag)));

      return matchesSearchText && matchesFilterType && matchesFilterTag;
    });
  }, [resources, searchText, typeFilter, tagFilter]);
};
